import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { ListRow } from "components/designV2/listRow/listRow";
import { Modal } from "components/designV2/modal/modal";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { schedule as scheduleService } from "services/api/company/schedule.service";
import type { AvailableReplacer } from "services/api/company/schedule.service";
import { getWorkerImg } from "services/firebase/firebase.service";
import {
  List,
  ListDescription,
  Radio,
  ReplaceLoader,
  ReplaceSpinner,
} from "./replaceModal.styled";
import { Api } from "services/api/api.service";
import { GigType } from "model/Gig";
import { normalizeUtcIso } from "web-apps/company/components/schedule/utils/schedule.dates";

interface ReplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReplaceSuccess?: () => void;
  companyId: number | null;
  workerId: number | null;
  workGroupId: number | null;
  startTime: string | null;
  endTime: string | null;
  breakStartTime: string | null;
  breakEndTime: string | null;
  breakTime: number | null;
  deductionAmount: number | null;
}

type AvatarResult = {
  externalId: string;
  url: string;
};

export const ReplaceModal: React.FC<ReplaceModalProps> = (props) => {
  const { t } = useTranslation();
  const [replacers, setReplacers] = useState<AvailableReplacer[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    let isMounted = true;
    if (!props.isOpen) {
      setReplacers([]);
      setSelectedId(null);
      setAvatarUrls({});
      setIsLoading(false);
      setIsSubmitting(false);
      return;
    }

    if (
      !props.companyId ||
      !props.workerId ||
      !props.workGroupId ||
      !props.startTime ||
      !props.endTime
    ) {
      setReplacers([]);
      setSelectedId(null);
      setAvatarUrls({});
      setIsLoading(false);
      return;
    }

    const companyId = props.companyId;
    const workerId = props.workerId;
    const workGroupId = props.workGroupId;
    const startTime = props.startTime;
    const endTime = props.endTime;
    const startTimeUtc = normalizeUtcIso(startTime);
    const endTimeUtc = normalizeUtcIso(endTime);
    if (!startTimeUtc.ok || !endTimeUtc.ok) {
      setReplacers([]);
      setSelectedId(null);
      setAvatarUrls({});
      setIsLoading(false);
      return;
    }

    const loadReplacers = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = await scheduleService.getAvailableReplacers(
          companyId,
          workerId,
          workGroupId,
          startTimeUtc.value,
          endTimeUtc.value,
        );
        if (!isMounted) return;
        const nextReplacers = response.data;
        setReplacers(nextReplacers);
        setSelectedId(null);

        const results = await Promise.all(
          nextReplacers.map(async (replacer): Promise<AvatarResult> => {
            const url = await getWorkerImg(replacer.externalId);
            return {
              externalId: replacer.externalId,
              url: url ?? "",
            };
          }),
        );
        if (!isMounted) return;
        const nextAvatarUrls = results.reduce<Record<string, string>>(
          (acc, entry) => {
            if (entry.url) acc[entry.externalId] = entry.url;
            return acc;
          },
          {},
        );
        setAvatarUrls(nextAvatarUrls);
      } finally {
        if (!isMounted) return;
        setIsLoading(false);
      }
    };

    void loadReplacers();
    return () => {
      isMounted = false;
    };
  }, [
    props.companyId,
    props.endTime,
    props.isOpen,
    props.startTime,
    props.workGroupId,
    props.workerId,
  ]);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <Modal
      label={t("Schedule.ChooseReplacement")}
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="small"
      allowContentOverflow
      footer={
        <PrimaryButton
          label={t("Schedule.Replace")}
          onClick={async () => {
            if (
              !props.companyId ||
              !props.workGroupId ||
              !selectedId ||
              !props.startTime ||
              !props.endTime ||
              isSubmitting
            )
              return;
            const payload = {
              workerId: selectedId,
              workGroupId: props.workGroupId,
              description: "",
              type: GigType.SOLO,
              shifts: [
                {
                  startTime: props.startTime,
                  endTime: props.endTime,
                  breakStartTime: props.breakStartTime ?? undefined,
                  breakEndTime: props.breakEndTime ?? undefined,
                  breakTime: props.breakTime ?? undefined,
                  deductionAmount: props.deductionAmount ?? undefined,
                },
              ],
            };
            setIsSubmitting(true);
            try {
              await Api().company.gig.createGig(props.companyId, payload);
              props.onReplaceSuccess?.();
            } finally {
              if (isMountedRef.current) setIsSubmitting(false);
            }
          }}
          disabled={!selectedId || isLoading || isSubmitting}
          isLoading={isSubmitting}
        />
      }
    >
      <>
        <ListDescription>{t("Schedule.ReplaceInfo")}</ListDescription>
        {isLoading ? (
          <ReplaceLoader>
            <ReplaceSpinner />
          </ReplaceLoader>
        ) : replacers.length === 0 ? (
          <Text typography="s1" color={ColorV2.Blue5} align="center">
            {t("Schedule.NoAvailableWorkers")}
          </Text>
        ) : (
          <List>
            {replacers.map((replacer) => {
              const name = `${replacer.firstName} ${replacer.lastName}`;
              return (
                <ListRow
                  key={replacer.id}
                  variant="dark"
                  selected={replacer.id === selectedId}
                  onClick={() => setSelectedId(replacer.id)}
                >
                  <AvatarChip
                    title={name}
                    avatarUrl={avatarUrls[replacer.externalId]}
                  />
                  <Radio
                    type="radio"
                    checked={replacer.id === selectedId}
                    onChange={() => setSelectedId(replacer.id)}
                  />
                </ListRow>
              );
            })}
          </List>
        )}
      </>
    </Modal>
  );
};
